import React from "react";
import { Button } from "@/components/ui/button";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Custom fallback UI to render when an error is caught */
  fallback?: React.ReactNode;
  /** Callback invoked when the user clicks retry — resets the boundary and re-renders children.
   *  If not provided, the default behavior is to reload the page. */
  onRetry?: () => void;
  /** Optional label override for the component (used in error reporting) */
  label?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const label = this.props.label || "ErrorBoundary";
    console.error(
      `[${label}] Caught error:`,
      error,
      "\nComponent stack:",
      errorInfo.componentStack
    );

    // Auto-reload on stale chunk errors (after deploy)
    if (
      error.message?.includes("Failed to fetch dynamically imported module") ||
      error.message?.includes("Loading chunk") ||
      error.message?.includes("MIME type")
    ) {
      const hasReloaded = sessionStorage.getItem("chunk_error_reload");
      if (!hasReloaded) {
        sessionStorage.setItem("chunk_error_reload", "1");
        window.location.reload();
        return;
      }
      sessionStorage.removeItem("chunk_error_reload");
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      const hasRetry = Boolean(this.props.onRetry);

      return (
        <div className="flex flex-col items-center justify-center p-8 text-center min-h-[200px]">
          <div className="size-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
            <svg className="size-6 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold mb-1">Algo salió mal</h3>
          <p className="text-xs text-muted-foreground mb-4 max-w-xs">
            Ha ocurrido un error inesperado. Intenta recargar la página.
          </p>
          <div className="flex items-center gap-2">
            {hasRetry && (
              <Button
                variant="default"
                size="sm"
                onClick={this.handleRetry}
              >
                Reintentar
              </Button>
            )}
            <Button
              variant={hasRetry ? "outline" : "default"}
              size="sm"
              onClick={hasRetry ? this.handleReload : () => {
                this.setState({ hasError: false, error: null });
                this.handleReload();
              }}
            >
              Recargar página
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
